import React, { useEffect, useState } from 'react';
import { ThemeContext, type Theme, designTokens } from './theme.context';

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Load saved theme from localStorage (purely client-side)
    const savedTheme = localStorage.getItem('theme');
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- localStorage can return null
    return (savedTheme as Theme) ?? 'system';
  });

  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    // Save theme preference to localStorage for immediate access on next load
    localStorage.setItem('theme', theme);

    // Determine effective theme
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      setEffectiveTheme(mediaQuery.matches ? 'dark' : 'light');

      // Listen for system theme changes
      const handleChange = (e: MediaQueryListEvent) => {
        setEffectiveTheme(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    setEffectiveTheme(theme);
    return undefined;
  }, [theme]);

  useEffect(() => {
    // Apply theme to document root
    if (effectiveTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [effectiveTheme]);

  return (
    <ThemeContext.Provider value={{ theme, effectiveTheme, setTheme, tokens: designTokens }}>
      {children}
    </ThemeContext.Provider>
  );
}
